import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  OrganizationDetailsData,
  ChartOfAccountsData,
  FiscalPeriodData,
  AdminUserData,
} from '../../pages/SetupWizardPage'

interface Step5SummaryProps {
  step1Data: OrganizationDetailsData | null
  step2Data: ChartOfAccountsData | null
  step3Data: FiscalPeriodData | null
  step4Data: AdminUserData | null
  onSubmit: () => void
  onBack: () => void
  loading: boolean
}

const TEMPLATE_NAMES: Record<string, string> = {
  association_basic: 'Yhdistys - Perus',
  association_extended: 'Yhdistys - Laajennettu',
  company: 'Yritys',
  custom: 'Mukautettu',
}

const TYPE_NAMES: Record<string, string> = {
  association: 'Yhdistys',
  company: 'Yritys',
  foundation: 'Säätiö',
  other: 'Muu',
}

export default function Step5Summary({
  step1Data,
  step2Data,
  step3Data,
  step4Data,
  onSubmit,
  onBack,
  loading,
}: Step5SummaryProps) {
  const { t } = useTranslation()
  const [confirmed, setConfirmed] = useState(false)

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('fi-FI')
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Yhteenveto</h2>
      <p className="text-gray-600 mb-6">Tarkista organisaatiosi tiedot ennen luontia.</p>

      <div className="space-y-6">
        {/* Organization details summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">1. Perustiedot</h3>
            <button
              onClick={() => onBack()}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Muokkaa
            </button>
          </div>
          {step1Data && (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Nimi</dt>
                <dd className="font-medium text-gray-900">{step1Data.name}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Tyyppi</dt>
                <dd className="font-medium text-gray-900">{TYPE_NAMES[step1Data.type]}</dd>
              </div>
              {step1Data.business_id && (
                <div>
                  <dt className="text-gray-500">Y-tunnus</dt>
                  <dd className="font-medium text-gray-900">{step1Data.business_id}</dd>
                </div>
              )}
              {step1Data.address && (
                <div className="md:col-span-2">
                  <dt className="text-gray-500">Osoite</dt>
                  <dd className="font-medium text-gray-900">
                    {step1Data.address}
                    {step1Data.postal_code && `, ${step1Data.postal_code}`}
                    {step1Data.city && ` ${step1Data.city}`}
                  </dd>
                </div>
              )}
              {step1Data.email && (
                <div>
                  <dt className="text-gray-500">Sähköposti</dt>
                  <dd className="font-medium text-gray-900">{step1Data.email}</dd>
                </div>
              )}
            </dl>
          )}
        </div>

        {/* Chart of accounts summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">2. Tilikartta</h3>
            <button
              onClick={() => onBack()}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Muokkaa
            </button>
          </div>
          {step2Data && (
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {TEMPLATE_NAMES[step2Data.template]}
              </p>
              <p className="text-gray-500 mt-1">
                {step2Data.template === 'custom'
                  ? t('setup.customChartLater')
                  : 'Valmiit tilit luodaan automaattisesti'}
              </p>
            </div>
          )}
        </div>

        {/* Fiscal period summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">3. Tilikausi</h3>
            <button
              onClick={() => onBack()}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Muokkaa
            </button>
          </div>
          {step3Data && (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Nimi</dt>
                <dd className="font-medium text-gray-900">{step3Data.name}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Alkamispäivä</dt>
                <dd className="font-medium text-gray-900">{formatDate(step3Data.start_date)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Päättymispäivä</dt>
                <dd className="font-medium text-gray-900">{formatDate(step3Data.end_date)}</dd>
              </div>
            </dl>
          )}
        </div>

        {/* Admin user summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">4. Admin-käyttäjä</h3>
            <button
              onClick={() => onBack()}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Muokkaa
            </button>
          </div>
          {step4Data && (
            <div className="text-sm">
              <p className="font-medium text-gray-900">{step4Data.email}</p>
              <p className="text-gray-500 mt-1">
                {step4Data.invite_only
                  ? 'Kutsu lähetetään sähköpostitse'
                  : 'Nykyinen käyttäjä asetetaan pääkäyttäjäksi'}
              </p>
            </div>
          )}
        </div>

        {/* Confirmation checkbox */}
        <div className="border-t border-gray-200 pt-6">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 mr-3"
            />
            <span className="text-sm text-gray-700">
              Vahvistan, että antamani tiedot ovat oikein ja haluan luoda organisaation.
              Ymmärrän, että organisaation luonnin jälkeen pääsen käyttämään Kirstunvartijaa.
            </span>
          </label>
        </div>

        {/* Error if not all data present */}
        {(!step1Data || !step2Data || !step3Data) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">
              <span className="font-medium">{t('setup.step5Title')}:</span>{' '}
              {!step1Data && `${t('setup.step1Title')} `}
              {!step2Data && `${t('setup.step2Title')} `}
              {!step3Data && `${t('setup.step3Title')} `}
              - {t('common.back')}
            </p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            ← Takaisin
          </button>
          <button
            onClick={onSubmit}
            disabled={loading || !confirmed || !step1Data || !step2Data || !step3Data}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Luodaan...
              </>
            ) : (
              <>
                <span className="mr-2">✓</span>
                Luo organisaatio
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
